import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import ReportsDashboard from './ReportsDashboard';

const AdminPanel = () => {
    const { user } = useContext(AuthContext);
    // User State
    const [users, setUsers] = useState([]);
    const [userData, setUserData] = useState({ email: '', password: '', role: 'standard', organization_id: '' });
    // Organization State
    const [organizations, setOrganizations] = useState([]);
    const [newOrgName, setNewOrgName] = useState('');
    
    // Vehicle State
    const [vehicles, setVehicles] = useState([]);
    const [vehicleData, setVehicleData] = useState({ truck_number: '', year: '', make: '', model: '', plate_number: '', organization_id: '' });
    const [editingVehicleId, setEditingVehicleId] = useState(null);
    const [editVehicleData, setEditVehicleData] = useState({});

    // Space State
    const [spaces, setSpaces] = useState([]);
    const [spaceData, setSpaceData] = useState({ space_number: '', group_name: '', organization_id: '' });
    const [editingSpaceId, setEditingSpaceId] = useState(null);
    const [editSpaceData, setEditSpaceData] = useState({});

    // Lots State
    const [lots, setLots] = useState([]);
    const [editingLotId, setEditingLotId] = useState(null);
    const [editLotName, setEditLotName] = useState('');
    const [editLotGeofenceId, setEditLotGeofenceId] = useState('');
    const [editLotIsPaid, setEditLotIsPaid] = useState(false);
    const [editLotPrice, setEditLotPrice] = useState('');
    const [editLotTimePeriod, setEditLotTimePeriod] = useState('DAILY');
    const [editLotResetTime, setEditLotResetTime] = useState('');

    // Logs State
    const [logs, setLogs] = useState([]);
    const [logsTotal, setLogsTotal] = useState(0);
    const [logsPage, setLogsPage] = useState(1);
    const [editingOrgId, setEditingOrgId] = useState(null);
    const [editOrgSecret, setEditOrgSecret] = useState('');

    const LOGS_PER_PAGE = 50;

    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    
    // CSV Upload State for Super Admins
    const [csvOrgId, setCsvOrgId] = useState('');

    const fetchData = async () => {
        try {
            const resVehicles = await axios.get('/vehicles');
            setVehicles(resVehicles.data);
            
            const resSpaces = await axios.get('/spaces');
            setSpaces(resSpaces.data);
            
            const resLots = await axios.get('/lots');
            setLots(resLots.data);

            if (user.role === 'admin' || user.role === 'org_admin') {
                const resUsers = await axios.get('/users');
                setUsers(resUsers.data);
                
                const resOrgs = await axios.get('/organizations');
                setOrganizations(resOrgs.data);
            }
        } catch (err) {
            console.error("Failed to load admin data", err);
        }
    };

    const fetchLogs = async () => {
        try {
            const skip = (logsPage - 1) * LOGS_PER_PAGE;
            const res = await axios.get('/logs', { params: { skip, limit: LOGS_PER_PAGE } });
            setLogs(res.data.logs);
            setLogsTotal(res.data.total_count);
        } catch (err) {
            console.error("Failed to fetch logs", err);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (user.role === 'admin' || user.role === 'org_admin') {
            fetchLogs();
        }
    }, [logsPage]);

    // --- CSV EXPORT UTILITY ---
    const downloadCSV = (data, filename) => {
        if (!data || !data.length) return;
        const headers = Object.keys(data[0]).join(',');
        const rows = data.map(obj => 
            Object.values(obj).map(val => {
                if (val && typeof val === 'object') return `"${JSON.stringify(val).replace(/"/g, '""')}"`;
                return `"${String(val).replace(/"/g, '""')}"`;
            }).join(',')
        ).join('\n');
        
        const csvContent = `${headers}\n${rows}`;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    const handleSpacesExport = () => {
        const exportData = spaces.map(s => ({
            "Space Number": s.space_number,
            "Zone": s.group_name,
            "Status": s.status,
            "Assigned Truck": s.vehicle ? s.vehicle.truck_number : "None",
            "Organization": organizations.find(o => o.id === s.organization_id)?.name || s.organization_id || "None"
        }));
        downloadCSV(exportData, 'spaces_export.csv');
    };

    const handleVehiclesExport = () => {
        const exportData = vehicles.map(v => ({
            "Truck Number": v.truck_number,
            "Year": v.year,
            "Make": v.make,
            "Model": v.model,
            "Plate Number": v.plate_number,
            "Motive ID": v.motive_vehicle_id || "",
            "Organization": organizations.find(o => o.id === v.organization_id)?.name || v.organization_id || "None"
        }));
        downloadCSV(exportData, 'vehicles_export.csv');
    };

    const handleUsersExport = () => {
        const exportData = users.map(u => ({
            "Email": u.email,
            "Role": u.role,
            "Status": u.is_active ? "Active" : "Disabled",
            "Organization": organizations.find(o => o.id === u.organization_id)?.name || (u.role === 'admin' ? "Super Admin" : "None")
        }));
        downloadCSV(exportData, 'users_export.csv');
    };
    const handleCSVUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (user.role === 'admin' && !csvOrgId) {
            alert("Super Admins must select an Organization from the dropdown next to the Upload button before uploading a CSV.");
            e.target.value = null;
            return;
        }

        const overwrite = window.confirm("If any truck numbers in the CSV already exist, do you want to OVERWRITE their data?\n\nClick OK to Overwrite, Cancel to Skip them.");
        
        const formData = new FormData();
        formData.append("file", file);
        formData.append("overwrite", overwrite);
        if (user.role === 'admin') {
            formData.append("organization_id", csvOrgId);
        }

        setMessage(''); setError('');
        try {
            const response = await axios.post('/vehicles/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setMessage(response.data.message);
            fetchData();
            fetchLogs();
        } catch (err) {
            setError(err.response?.data?.detail || "Failed to upload CSV.");
        }
        e.target.value = null; // reset input
    };

    const handleSpaceCSVUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (user.role === 'admin' && !csvOrgId) {
            alert("Super Admins must select an Organization from the dropdown next to the Upload button before uploading a CSV.");
            e.target.value = null;
            return;
        }

        const overwrite = window.confirm("If any space numbers in the CSV already exist, do you want to OVERWRITE their Zone/Group data?\n\nClick OK to Overwrite, Cancel to Skip them.");
        
        const formData = new FormData();
        formData.append("file", file);
        formData.append("overwrite", overwrite);
        if (user.role === 'admin') {
            formData.append("organization_id", csvOrgId);
        }

        setMessage(''); setError('');
        try {
            const response = await axios.post('/spaces/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setMessage(response.data.message);
            fetchData();
            fetchLogs();
        } catch (err) {
            setError(err.response?.data?.detail || "Failed to upload CSV.");
        }
        e.target.value = null; // reset input
    };

    // --- ORGANIZATION MANAGEMENT ---
    const handleOrgSubmit = async (e) => {
        e.preventDefault();
        setMessage(''); setError('');
        try {
            const response = await axios.post('/organizations', { name: newOrgName });
            setMessage(`Organization ${response.data.name} created successfully.`);
            setNewOrgName('');
            fetchData();
            fetchLogs();
        } catch (err) { setError(err.response?.data?.detail || "Failed to add organization."); }
    };

    const deleteOrg = async (orgId) => {
        if (!window.confirm("Are you sure you want to delete this organization?")) return;
        setMessage(''); setError('');
        try {
            await axios.delete(`/organizations/${orgId}`);
            setMessage("Organization deleted successfully.");
            fetchData();
            fetchLogs();
        } catch (err) { setError(err.response?.data?.detail || "Failed to delete organization."); }
    };

    const startOrgEdit = (org) => {
        setEditingOrgId(org.id);
        setEditOrgSecret(org.motive_webhook_secret || '');
    };

    const saveOrgEdit = async (orgId) => {
        setMessage(''); setError('');
        try {
            await axios.patch(`/organizations/${orgId}`, { motive_webhook_secret: editOrgSecret });
            setMessage("Organization updated successfully.");
            setEditingOrgId(null);
            fetchData();
        } catch(err) { setError("Failed to update organization."); }
    };

    // --- LOT MANAGEMENT ---
    const startLotEdit = (lot) => {
        setEditingLotId(lot.id);
        setEditLotName(lot.lot_name);
        setEditLotGeofenceId(lot.motive_geofence_id || '');
        setEditLotIsPaid(lot.is_paid_lot || false);
        setEditLotPrice(lot.price_per_spot ? (lot.price_per_spot / 100).toFixed(2) : '');
        setEditLotTimePeriod(lot.time_period || 'DAILY');
        setEditLotResetTime(lot.reset_time ? lot.reset_time.substring(0, 5) : '');
    };

    const saveLotEdit = async (lotId) => {
        setMessage(''); setError('');
        try {
            await axios.patch(`/lots/${lotId}`, { 
                lot_name: editLotName,
                motive_geofence_id: editLotGeofenceId,
                is_paid_lot: editLotIsPaid,
                price_per_spot: editLotPrice ? parseInt(parseFloat(editLotPrice) * 100) : null,
                time_period: editLotTimePeriod,
                reset_time: editLotResetTime || null
            });
            setMessage("Lot updated successfully.");
            setEditingLotId(null);
            fetchData();
        } catch(err) { setError("Failed to update lot."); }
    };

    // --- USER MANAGEMENT ---
    const handleUserSubmit = async (e) => {
        e.preventDefault();
        setMessage(''); setError('');
        try {
            const response = await axios.post('/users', userData);
            setMessage(response.data.message);
            setUserData({ email: '', password: '', role: 'standard' });
            fetchData();
            fetchLogs(); // refresh logs to show the create action
        } catch (err) { setError(err.response?.data?.detail || "Failed to add user."); }
    };

    const toggleUserRole = async (userId, currentRole) => {
        let roles = ['standard', 'manager', 'org_admin', 'admin'];
        if (user.role === 'org_admin') {
            roles = ['standard', 'manager', 'org_admin'];
        }
        const currentIndex = roles.indexOf(currentRole);
        const newRole = currentIndex === -1 ? 'standard' : roles[(currentIndex + 1) % roles.length];
        try {
            await axios.patch(`/users/${userId}/role`, { role: newRole });
            fetchData();
            fetchLogs();
            setMessage("User role updated successfully");
        } catch (err) { setError(err.response?.data?.detail || "Failed to update role"); }
    };

    const updateUserOrg = async (userId, orgId) => {
        try {
            await axios.patch(`/users/${userId}/organization`, { organization_id: orgId ? parseInt(orgId) : null });
            fetchData();
            fetchLogs();
            setMessage("User organization updated successfully");
        } catch (err) { setError(err.response?.data?.detail || "Failed to update organization"); }
    };

    const toggleUserActive = async (userId, currentState) => {
        try {
            const response = await axios.patch(`/users/${userId}/active`, { is_active: !currentState });
            fetchData();
            fetchLogs();
            setMessage(response.data.message);
        } catch (err) { setError(err.response?.data?.detail || "Failed to update active status"); }
    };

    // --- VEHICLE MANAGEMENT ---
    const handleVehicleSubmit = async (e) => {
        e.preventDefault();
        setMessage(''); setError('');
        try {
            const response = await axios.post('/vehicles', { ...vehicleData, year: parseInt(vehicleData.year) });
            setMessage(response.data.message);
            setVehicleData({ truck_number: '', year: '', make: '', model: '', plate_number: '', organization_id: '' }); 
            fetchData();
            fetchLogs();
        } catch (err) { setError(err.response?.data?.detail || "Failed to add vehicle."); }
    };

    const startVehicleEdit = (vehicle) => {
        setEditingVehicleId(vehicle.id);
        setEditVehicleData({ ...vehicle });
    };

    const saveVehicleEdit = async (vehicleId) => {
        setMessage(''); setError('');
        try {
            await axios.put(`/vehicles/${vehicleId}`, { ...editVehicleData, year: parseInt(editVehicleData.year) });
            setMessage("Vehicle updated successfully");
            setEditingVehicleId(null);
            fetchData();
            fetchLogs();
        } catch (err) { setError(err.response?.data?.detail || "Failed to update vehicle."); }
    };

    const deleteVehicle = async (vehicleId) => {
        if (!window.confirm("Are you sure you want to delete this vehicle? Any spaces it occupies will be released.")) return;
        setMessage(''); setError('');
        try {
            await axios.delete(`/vehicles/${vehicleId}`);
            setMessage("Vehicle deleted successfully");
            fetchData();
            fetchLogs();
        } catch (err) { setError(err.response?.data?.detail || "Failed to delete vehicle."); }
    };

    // --- SPACE MANAGEMENT ---
    const handleSpaceSubmit = async (e) => {
        e.preventDefault();
        setMessage(''); setError('');
        try {
            const response = await axios.post('/spaces', spaceData);
            setMessage(response.data.message);
            setSpaceData({ ...spaceData, space_number: '', group_name: '' }); 
            fetchData();
            fetchLogs();
        } catch (err) { setError(err.response?.data?.detail || "Failed to add space."); }
    };

    const startSpaceEdit = (space) => {
        setEditingSpaceId(space.id);
        setEditSpaceData({ ...space });
    };

    const saveSpaceEdit = async (spaceId) => {
        setMessage(''); setError('');
        try {
            await axios.put(`/spaces/${spaceId}`, { 
                space_number: editSpaceData.space_number,
                group_name: editSpaceData.group_name
            });
            setMessage("Space updated successfully");
            setEditingSpaceId(null);
            fetchData();
            fetchLogs();
        } catch (err) { setError(err.response?.data?.detail || "Failed to update space."); }
    };

    const deleteSpace = async (spaceId) => {
        if (!window.confirm("Are you sure you want to delete this space?")) return;
        setMessage(''); setError('');
        try {
            await axios.delete(`/spaces/${spaceId}`);
            setMessage("Space deleted successfully");
            fetchData();
            fetchLogs();
        } catch (err) { setError(err.response?.data?.detail || "Failed to delete space."); }
    };

    const totalPages = Math.ceil(logsTotal / LOGS_PER_PAGE);
    
    // Derive unique zones for the dropdown
    const uniqueZones = Array.from(new Set(spaces.map(s => s.group_name))).sort();

    return (
        <div className="container">
            <div className="header">
                <h1 style={{ margin: 0 }}>Admin Control Panel</h1>
                <Link to="/" className="btn" style={{ textDecoration: 'none', backgroundColor: 'var(--input-border)', color: 'var(--text-color)' }}>← Back to Dashboard</Link>
            </div>
            
            {message && <div style={{ color: 'var(--success)', marginBottom: '15px', padding: '10px', backgroundColor: 'var(--success-bg)', borderRadius: '8px' }}>{message}</div>}
            {error && <div style={{ color: 'var(--danger)', marginBottom: '15px', padding: '10px', backgroundColor: 'var(--danger-hover)', borderRadius: '8px', color: 'white' }}>{error}</div>}

            <ReportsDashboard />

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '30px' }}>
                
                {/* --- ORGANIZATION MANAGEMENT --- */}
                {(user.role === 'admin' || user.role === 'org_admin') && (
                <div className="glass-card" style={{ padding: '20px', gridColumn: '1 / -1' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--card-border)', paddingBottom: '10px', marginBottom: '15px' }}>
                        <h3 style={{ margin: 0 }}>Organization Settings</h3>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                        {user.role === 'admin' && (
                        <div style={{ flex: '1', minWidth: '300px' }}>
                            <form onSubmit={handleOrgSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <input type="text" placeholder="Organization Name" value={newOrgName} onChange={e => setNewOrgName(e.target.value)} required />
                                <button type="submit" className="btn btn-primary">Create Organization</button>
                            </form>
                        </div>
                        )}
                        <div style={{ flex: '2', minWidth: '400px', overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid var(--card-border)' }}>
                                        <th style={{ padding: '10px' }}>ID</th>
                                        <th style={{ padding: '10px' }}>Organization Name</th>
                                        <th style={{ padding: '10px' }}>Motive Webhook Secret</th>
                                        <th style={{ padding: '10px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {organizations.map(org => (
                                        <tr key={org.id} style={{ borderBottom: '1px solid var(--card-border)' }}>
                                            {editingOrgId === org.id ? (
                                                <>
                                                    <td style={{ padding: '10px' }}>{org.id}</td>
                                                    <td style={{ padding: '10px' }}>{org.name}</td>
                                                    <td style={{ padding: '10px' }}><input type="text" placeholder="Webhook Secret" value={editOrgSecret} onChange={e => setEditOrgSecret(e.target.value)} style={{ padding: '5px' }} /></td>
                                                    <td style={{ padding: '10px', display: 'flex', gap: '5px' }}>
                                                        <button onClick={() => saveOrgEdit(org.id)} className="btn btn-primary" style={{ padding: '5px 10px', fontSize: '12px' }}>Save</button>
                                                        <button onClick={() => setEditingOrgId(null)} className="btn" style={{ padding: '5px 10px', fontSize: '12px', backgroundColor: 'var(--input-border)' }}>Cancel</button>
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td style={{ padding: '10px' }}>{org.id}</td>
                                                    <td style={{ padding: '10px' }}>{org.name}</td>
                                                    <td style={{ padding: '10px' }}>{org.motive_webhook_secret ? "••••••••" : "Not Set"}</td>
                                                    <td style={{ padding: '10px', display: 'flex', gap: '5px' }}>
                                                        <button onClick={() => startOrgEdit(org)} className="btn" style={{ padding: '5px 10px', fontSize: '12px', backgroundColor: 'var(--primary)', color: 'white' }}>Edit</button>
                                                        {user.role === 'admin' && (
                                                            <button onClick={() => deleteOrg(org.id)} className="btn btn-danger" style={{ padding: '5px 10px', fontSize: '12px' }}>Delete</button>
                                                        )}
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {organizations.length > 0 && (
                                <p style={{ fontSize: '12px', color: 'var(--text-color)', marginTop: '10px', fontStyle: 'italic' }}>
                                    Your Webhook URL is: <strong>{window.location.origin}/webhooks/motive/{organizations[0].id}</strong>
                                </p>
                            )}
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--card-border)', paddingBottom: '10px', marginBottom: '15px', marginTop: '30px' }}>
                        <h3 style={{ margin: 0 }}>Lot Settings</h3>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                        <div style={{ flex: '1', minWidth: '400px', overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid var(--card-border)' }}>
                                        <th style={{ padding: '10px' }}>Lot Name</th>
                                        <th style={{ padding: '10px' }}>Paid Lot</th>
                                        <th style={{ padding: '10px' }}>Price</th>
                                        <th style={{ padding: '10px' }}>Period</th>
                                        <th style={{ padding: '10px' }}>Reset Time</th>
                                        <th style={{ padding: '10px' }}>Motive Geofence ID</th>
                                        {user.role === 'admin' && <th style={{ padding: '10px' }}>Organization</th>}
                                        <th style={{ padding: '10px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lots.map(lot => (
                                        <tr key={lot.id} style={{ borderBottom: '1px solid var(--card-border)' }}>
                                            {editingLotId === lot.id ? (
                                                <>
                                                    <td style={{ padding: '10px' }}><input type="text" placeholder="Lot Name" value={editLotName} onChange={e => setEditLotName(e.target.value)} style={{ padding: '5px' }} /></td>
                                                    <td style={{ padding: '10px' }}><input type="checkbox" checked={editLotIsPaid} onChange={e => setEditLotIsPaid(e.target.checked)} disabled={user.role !== 'admin'} /></td>
                                                    <td style={{ padding: '10px' }}><input type="number" step="0.01" placeholder="Price" value={editLotPrice} onChange={e => setEditLotPrice(e.target.value)} style={{ padding: '5px', width: '60px' }} /></td>
                                                    <td style={{ padding: '10px' }}>
                                                        <select value={editLotTimePeriod} onChange={e => setEditLotTimePeriod(e.target.value)} style={{ padding: '5px' }}>
                                                            <option value="DAILY">Daily</option>
                                                            <option value="MONTHLY">Monthly</option>
                                                        </select>
                                                    </td>
                                                    <td style={{ padding: '10px' }}><input type="time" value={editLotResetTime} onChange={e => setEditLotResetTime(e.target.value)} style={{ padding: '5px' }} /></td>
                                                    <td style={{ padding: '10px' }}><input type="text" placeholder="Geofence ID" value={editLotGeofenceId} onChange={e => setEditLotGeofenceId(e.target.value)} style={{ padding: '5px', width: '100px' }} /></td>
                                                    {user.role === 'admin' && <td style={{ padding: '10px' }}>{organizations.find(o => o.id === lot.organization_id)?.name || 'Unknown'}</td>}
                                                    <td style={{ padding: '10px', display: 'flex', gap: '5px' }}>
                                                        <button onClick={() => saveLotEdit(lot.id)} className="btn btn-primary" style={{ padding: '5px 10px', fontSize: '12px' }}>Save</button>
                                                        <button onClick={() => setEditingLotId(null)} className="btn" style={{ padding: '5px 10px', fontSize: '12px', backgroundColor: 'var(--input-border)' }}>Cancel</button>
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td style={{ padding: '10px' }}>{lot.lot_name}</td>
                                                    <td style={{ padding: '10px' }}>{lot.is_paid_lot ? "Yes" : "No"}</td>
                                                    <td style={{ padding: '10px' }}>{lot.price_per_spot ? `$${(lot.price_per_spot / 100).toFixed(2)}` : "-"}</td>
                                                    <td style={{ padding: '10px' }}>{lot.time_period || "-"}</td>
                                                    <td style={{ padding: '10px' }}>{lot.reset_time ? lot.reset_time.substring(0, 5) : "-"}</td>
                                                    <td style={{ padding: '10px' }}>{lot.motive_geofence_id || "Not Set"}</td>
                                                    {user.role === 'admin' && <td style={{ padding: '10px' }}>{organizations.find(o => o.id === lot.organization_id)?.name || 'Unknown'}</td>}
                                                    <td style={{ padding: '10px', display: 'flex', gap: '5px' }}>
                                                        <button onClick={() => startLotEdit(lot)} className="btn" style={{ padding: '5px 10px', fontSize: '12px', backgroundColor: 'var(--primary)', color: 'white' }}>Edit</button>
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                )}

                {/* --- USER MANAGEMENT --- */}
                {(user.role === 'admin' || user.role === 'org_admin') && (
                <div className="glass-card" style={{ padding: '20px', gridColumn: '1 / -1' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--card-border)', paddingBottom: '10px', marginBottom: '15px' }}>
                        <h3 style={{ margin: 0 }}>User Management</h3>
                        <button onClick={handleUsersExport} className="btn" style={{ backgroundColor: 'var(--success)', fontSize: '12px', padding: '5px 10px' }}>
                            Download CSV
                        </button>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                        <div style={{ flex: '1', minWidth: '300px' }}>
                            <form onSubmit={handleUserSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <input type="email" placeholder="Email" value={userData.email} onChange={e => setUserData({...userData, email: e.target.value})} required />
                                <input type="password" placeholder="Password" value={userData.password} onChange={e => setUserData({...userData, password: e.target.value})} required />
                                <select value={userData.role} onChange={e => setUserData({...userData, role: e.target.value})}>
                                    <option value="standard">Standard</option>
                                    <option value="manager">Manager</option>
                                    <option value="org_admin">Org Admin</option>
                                    {user.role === 'admin' && (
                                        <option value="admin">Admin</option>
                                    )}
                                </select>
                                {user.role === 'admin' && (
                                    <select value={userData.organization_id} onChange={e => setUserData({...userData, organization_id: e.target.value})}>
                                        <option value="">No Organization (Super Admin Only)</option>
                                        {organizations.map(org => (
                                            <option key={org.id} value={org.id}>{org.name}</option>
                                        ))}
                                    </select>
                                )}
                                <button type="submit" className="btn btn-primary">Create User</button>
                            </form>
                        </div>
                        <div style={{ flex: '2', minWidth: '400px', overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid var(--card-border)' }}>
                                        <th style={{ padding: '10px' }}>ID</th>
                                        <th style={{ padding: '10px' }}>Email</th>
                                        <th style={{ padding: '10px' }}>Status</th>
                                        <th style={{ padding: '10px' }}>Organization</th>
                                        <th style={{ padding: '10px' }}>Role</th>
                                        <th style={{ padding: '10px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map(u => (
                                        <tr key={u.id} style={{ borderBottom: '1px solid var(--card-border)' }}>
                                            <td style={{ padding: '10px' }}>{u.id}</td>
                                            <td style={{ padding: '10px' }}>{u.email}</td>
                                            <td style={{ padding: '10px' }}>
                                                <span style={{ color: u.is_active ? 'var(--success)' : 'var(--danger)', fontWeight: 'bold' }}>
                                                    {u.is_active ? 'Active' : 'Disabled'}
                                                </span>
                                            </td>
                                            <td style={{ padding: '10px' }}>
                                                {user.role === 'admin' ? (
                                                    <select 
                                                        value={u.organization_id || ""} 
                                                        onChange={(e) => updateUserOrg(u.id, e.target.value)}
                                                        style={{ padding: '4px', borderRadius: '4px', border: '1px solid var(--input-border)', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)' }}
                                                    >
                                                        <option value="">None</option>
                                                        {organizations.map(org => (
                                                            <option key={org.id} value={org.id}>{org.name}</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    organizations.find(o => o.id === u.organization_id)?.name || "None"
                                                )}
                                            </td>
                                            <td style={{ padding: '10px' }}>
                                                <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: u.role === 'admin' ? 'var(--warning-bg)' : 'var(--input-bg)', color: u.role === 'admin' ? 'var(--warning)' : 'var(--text-color)', fontSize: '12px', fontWeight: 'bold' }}>
                                                    {u.role.toUpperCase()}
                                                </span>
                                            </td>
                                            <td style={{ padding: '10px', display: 'flex', gap: '5px' }}>
                                                <button onClick={() => toggleUserRole(u.id, u.role)} className="btn" style={{ padding: '5px 10px', fontSize: '12px', backgroundColor: 'var(--input-border)', color: 'var(--text-color)' }}>
                                                    Role
                                                </button>
                                                <button onClick={() => toggleUserActive(u.id, u.is_active)} className={`btn ${u.is_active ? 'btn-danger' : 'btn-primary'}`} style={{ padding: '5px 10px', fontSize: '12px' }}>
                                                    {u.is_active ? 'Disable' : 'Enable'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                )}

                {/* --- SPACE MANAGEMENT --- */}
                <div className="glass-card" style={{ padding: '20px', gridColumn: '1 / -1' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--card-border)', paddingBottom: '10px', marginBottom: '15px' }}>
                        <h3 style={{ margin: 0 }}>Parking Infrastructure Management</h3>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            {user.role === 'admin' && (
                                <select value={csvOrgId} onChange={e => setCsvOrgId(e.target.value)} style={{ marginRight: '10px', padding: '5px', borderRadius: '4px', border: '1px solid var(--input-border)', backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)' }}>
                                    <option value="">Select Org for CSV</option>
                                    {organizations.map(org => (
                                        <option key={org.id} value={org.id}>{org.name}</option>
                                    ))}
                                </select>
                            )}
                            <input type="file" accept=".csv" id="spaces-csv-upload" style={{ display: 'none' }} onChange={handleSpaceCSVUpload} />
                            <button onClick={handleSpacesExport} className="btn" style={{ backgroundColor: 'var(--success)', fontSize: '12px', padding: '5px 10px' }}>
                                Download CSV
                            </button>
                            <button onClick={() => document.getElementById('spaces-csv-upload').click()} className="btn" style={{ backgroundColor: 'var(--primary)', fontSize: '12px', padding: '5px 10px', marginLeft: '10px' }}>
                                Upload CSV
                            </button>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                        {/* New Space Form */}
                        <div style={{ flex: '1', minWidth: '300px' }}>
                            <form onSubmit={handleSpaceSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <h4>Create New Parking Space</h4>
                                <input type="text" placeholder="Space Number (e.g., A4)" value={spaceData.space_number} onChange={e => setSpaceData({...spaceData, space_number: e.target.value})} required />
                                {user.role === 'admin' || user.role === 'org_admin' ? (
                                    <input type="text" placeholder="Group / Zone (e.g., Front Fence)" value={spaceData.group_name} onChange={e => setSpaceData({...spaceData, group_name: e.target.value})} required />
                                ) : (
                                    <select value={spaceData.group_name} onChange={e => setSpaceData({...spaceData, group_name: e.target.value})} required>
                                        <option value="" disabled>Select a Zone</option>
                                        {uniqueZones.map(zone => (
                                            <option key={zone} value={zone}>{zone}</option>
                                        ))}
                                    </select>
                                )}
                                {user.role === 'admin' && (
                                    <select value={spaceData.organization_id} onChange={e => setSpaceData({...spaceData, organization_id: e.target.value})} required>
                                        <option value="" disabled>Select Organization</option>
                                        {organizations.map(org => (
                                            <option key={org.id} value={org.id}>{org.name}</option>
                                        ))}
                                    </select>
                                )}
                                <button type="submit" className="btn btn-primary">Add Space</button>
                            </form>
                        </div>
                        
                        {/* Space Table */}
                        <div style={{ flex: '3', minWidth: '500px', overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid var(--card-border)' }}>
                                        <th style={{ padding: '8px' }}>Space #</th>
                                        <th style={{ padding: '8px' }}>Zone</th>
                                        <th style={{ padding: '8px' }}>Status</th>
                                        <th style={{ padding: '8px' }}>Assigned Truck</th>
                                        {user.role === 'admin' && <th style={{ padding: '8px' }}>Organization</th>}
                                        <th style={{ padding: '8px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {spaces.map(s => (
                                        <tr key={s.id} style={{ borderBottom: '1px solid var(--card-border)' }}>
                                            {editingSpaceId === s.id ? (
                                                <>
                                                    <td style={{ padding: '5px' }}><input type="text" value={editSpaceData.space_number} onChange={e => setEditSpaceData({...editSpaceData, space_number: e.target.value})} style={{ padding: '5px' }}/></td>
                                                    <td style={{ padding: '5px' }}>
                                                        {user.role === 'admin' || user.role === 'org_admin' ? (
                                                            <input type="text" value={editSpaceData.group_name} onChange={e => setEditSpaceData({...editSpaceData, group_name: e.target.value})} style={{ padding: '5px' }} />
                                                        ) : (
                                                            <select value={editSpaceData.group_name} onChange={e => setEditSpaceData({...editSpaceData, group_name: e.target.value})} style={{ padding: '5px' }}>
                                                                {uniqueZones.map(zone => (
                                                                    <option key={zone} value={zone}>{zone}</option>
                                                                ))}
                                                            </select>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '5px' }}>{s.status}</td>
                                                    <td style={{ padding: '5px' }}>{s.vehicle ? `#${s.vehicle.truck_number}` : 'None'}</td>
                                                    {user.role === 'admin' && <td style={{ padding: '5px' }}></td>}
                                                    <td style={{ padding: '5px', display: 'flex', gap: '5px' }}>
                                                        <button onClick={() => saveSpaceEdit(s.id)} className="btn btn-primary" style={{ padding: '5px 10px', fontSize: '12px' }}>Save</button>
                                                        <button onClick={() => setEditingSpaceId(null)} className="btn" style={{ padding: '5px 10px', fontSize: '12px', backgroundColor: 'var(--input-border)' }}>Cancel</button>
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td style={{ padding: '8px', fontWeight: 'bold' }}>{s.space_number}</td>
                                                    <td style={{ padding: '8px' }}>{s.group_name}</td>
                                                    <td style={{ padding: '8px', color: s.status === 'GREEN' ? 'var(--success)' : s.status === 'OUT_OF_SERVICE' ? 'var(--out-of-service)' : 'var(--danger)', fontWeight: 'bold' }}>
                                                        {s.status}
                                                    </td>
                                                    <td style={{ padding: '8px' }}>{s.vehicle ? `#${s.vehicle.truck_number}` : 'None'}</td>
                                                    {user.role === 'admin' && <td style={{ padding: '8px' }}>{organizations.find(o => o.id === s.organization_id)?.name || 'Unknown'}</td>}
                                                    <td style={{ padding: '8px', display: 'flex', gap: '5px' }}>
                                                        <button onClick={() => startSpaceEdit(s)} className="btn" style={{ padding: '5px 10px', fontSize: '12px', backgroundColor: 'var(--primary)', color: 'white' }}>Edit</button>
                                                        {(user.role === 'admin' || user.role === 'org_admin') && (
                                                            <button onClick={() => deleteSpace(s.id)} className="btn btn-danger" style={{ padding: '5px 10px', fontSize: '12px' }}>Delete</button>
                                                        )}
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* --- VEHICLE MANAGEMENT --- */}
                <div className="glass-card" style={{ padding: '20px', gridColumn: '1 / -1' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--card-border)', paddingBottom: '10px', marginBottom: '15px' }}>
                        <h3 style={{ margin: 0 }}>Vehicle Fleet Management</h3>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            {user.role === 'admin' && (
                                <select value={csvOrgId} onChange={e => setCsvOrgId(e.target.value)} style={{ marginRight: '10px', padding: '5px', borderRadius: '4px', border: '1px solid var(--input-border)', backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)' }}>
                                    <option value="">Select Org for CSV</option>
                                    {organizations.map(org => (
                                        <option key={org.id} value={org.id}>{org.name}</option>
                                    ))}
                                </select>
                            )}
                            <input type="file" accept=".csv" id="csv-upload" style={{ display: 'none' }} onChange={handleCSVUpload} />
                            <button onClick={handleVehiclesExport} className="btn" style={{ backgroundColor: 'var(--success)', fontSize: '12px', padding: '5px 10px' }}>
                                Download CSV
                            </button>
                            <button onClick={() => document.getElementById('csv-upload').click()} className="btn" style={{ backgroundColor: 'var(--primary)', fontSize: '12px', padding: '5px 10px', marginLeft: '10px' }}>
                                Upload CSV
                            </button>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                        {/* New Vehicle Form */}
                        <div style={{ flex: '1', minWidth: '300px' }}>
                            <form onSubmit={handleVehicleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <h4>Register New Truck</h4>
                                <input type="text" placeholder="Truck # (e.g., 99)" value={vehicleData.truck_number} onChange={e => setVehicleData({...vehicleData, truck_number: e.target.value})} required />
                                <input type="number" placeholder="Year" value={vehicleData.year} onChange={e => setVehicleData({...vehicleData, year: e.target.value})} required />
                                <input type="text" placeholder="Make" value={vehicleData.make} onChange={e => setVehicleData({...vehicleData, make: e.target.value})} required />
                                <input type="text" placeholder="Model" value={vehicleData.model} onChange={e => setVehicleData({...vehicleData, model: e.target.value})} required />
                                <input type="text" placeholder="Plate Number" value={vehicleData.plate_number} onChange={e => setVehicleData({...vehicleData, plate_number: e.target.value})} required />
                                <input type="text" placeholder="Motive Vehicle ID (Optional)" value={vehicleData.motive_vehicle_id || ''} onChange={e => setVehicleData({...vehicleData, motive_vehicle_id: e.target.value})} />
                                {user.role === 'admin' && (
                                    <select value={vehicleData.organization_id} onChange={e => setVehicleData({...vehicleData, organization_id: e.target.value})} required>
                                        <option value="" disabled>Select Organization</option>
                                        {organizations.map(org => (
                                            <option key={org.id} value={org.id}>{org.name}</option>
                                        ))}
                                    </select>
                                )}
                                <button type="submit" className="btn btn-primary">Add Vehicle</button>
                            </form>
                        </div>
                        
                        {/* Vehicle Table */}
                        <div style={{ flex: '3', minWidth: '500px', overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid var(--card-border)' }}>
                                        <th style={{ padding: '8px' }}>Truck #</th>
                                        <th style={{ padding: '8px' }}>Year</th>
                                        <th style={{ padding: '8px' }}>Make</th>
                                        <th style={{ padding: '8px' }}>Model</th>
                                        <th style={{ padding: '8px' }}>Plate</th>
                                        <th style={{ padding: '8px' }}>Motive ID</th>
                                        {user.role === 'admin' && <th style={{ padding: '8px' }}>Organization</th>}
                                        <th style={{ padding: '8px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {vehicles.map(v => (
                                        <tr key={v.id} style={{ borderBottom: '1px solid var(--card-border)' }}>
                                            {editingVehicleId === v.id ? (
                                                <>
                                                    <td style={{ padding: '5px' }}><input type="text" value={editVehicleData.truck_number} onChange={e => setEditVehicleData({...editVehicleData, truck_number: e.target.value})} style={{ padding: '5px' }}/></td>
                                                    <td style={{ padding: '5px' }}><input type="number" value={editVehicleData.year} onChange={e => setEditVehicleData({...editVehicleData, year: e.target.value})} style={{ padding: '5px' }}/></td>
                                                    <td style={{ padding: '5px' }}><input type="text" value={editVehicleData.make} onChange={e => setEditVehicleData({...editVehicleData, make: e.target.value})} style={{ padding: '5px' }}/></td>
                                                    <td style={{ padding: '5px' }}><input type="text" value={editVehicleData.model} onChange={e => setEditVehicleData({...editVehicleData, model: e.target.value})} style={{ padding: '5px' }}/></td>
                                                    <td style={{ padding: '5px' }}><input type="text" value={editVehicleData.plate_number} onChange={e => setEditVehicleData({...editVehicleData, plate_number: e.target.value})} style={{ padding: '5px' }}/></td>
                                                    <td style={{ padding: '5px' }}><input type="text" placeholder="Optional" value={editVehicleData.motive_vehicle_id || ''} onChange={e => setEditVehicleData({...editVehicleData, motive_vehicle_id: e.target.value})} style={{ padding: '5px' }}/></td>
                                                    {user.role === 'admin' && <td style={{ padding: '5px' }}></td>}
                                                    <td style={{ padding: '5px', display: 'flex', gap: '5px' }}>
                                                        <button onClick={() => saveVehicleEdit(v.id)} className="btn btn-primary" style={{ padding: '5px 10px', fontSize: '12px' }}>Save</button>
                                                        <button onClick={() => setEditingVehicleId(null)} className="btn" style={{ padding: '5px 10px', fontSize: '12px', backgroundColor: 'var(--input-border)' }}>Cancel</button>
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td style={{ padding: '8px', fontWeight: 'bold' }}>{v.truck_number}</td>
                                                    <td style={{ padding: '8px' }}>{v.year}</td>
                                                    <td style={{ padding: '8px' }}>{v.make}</td>
                                                    <td style={{ padding: '8px' }}>{v.model}</td>
                                                    <td style={{ padding: '8px' }}>{v.plate_number}</td>
                                                    <td style={{ padding: '8px' }}>{v.motive_vehicle_id || 'None'}</td>
                                                    {user.role === 'admin' && <td style={{ padding: '8px' }}>{organizations.find(o => o.id === v.organization_id)?.name || 'Unknown'}</td>}
                                                    <td style={{ padding: '8px', display: 'flex', gap: '5px' }}>
                                                        <button onClick={() => startVehicleEdit(v)} className="btn" style={{ padding: '5px 10px', fontSize: '12px', backgroundColor: 'var(--primary)', color: 'white' }}>Edit</button>
                                                        {(user.role === 'admin' || user.role === 'org_admin') && (
                                                            <button onClick={() => deleteVehicle(v.id)} className="btn btn-danger" style={{ padding: '5px 10px', fontSize: '12px' }}>Delete</button>
                                                        )}
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                
                {/* --- AUDIT LOGS --- */}
                {(user.role === 'admin' || user.role === 'org_admin') && (
                <div className="glass-card" style={{ padding: '20px', gridColumn: '1 / -1' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--card-border)', paddingBottom: '10px', marginBottom: '15px' }}>
                        <h3 style={{ margin: 0 }}>System Audit Logs</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <button 
                                onClick={() => setLogsPage(p => Math.max(1, p - 1))} 
                                disabled={logsPage === 1}
                                className="btn" 
                                style={{ padding: '5px 10px', fontSize: '12px', backgroundColor: 'var(--input-border)', color: 'var(--text-color)' }}
                            >
                                Prev
                            </button>
                            <span style={{ fontSize: '14px' }}>Page {logsPage} of {totalPages || 1}</span>
                            <button 
                                onClick={() => setLogsPage(p => Math.min(totalPages, p + 1))} 
                                disabled={logsPage >= totalPages}
                                className="btn" 
                                style={{ padding: '5px 10px', fontSize: '12px', backgroundColor: 'var(--input-border)', color: 'var(--text-color)' }}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid var(--card-border)' }}>
                                    <th style={{ padding: '8px' }}>Timestamp</th>
                                    <th style={{ padding: '8px' }}>User</th>
                                    <th style={{ padding: '8px' }}>Action</th>
                                    <th style={{ padding: '8px' }}>Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(log => (
                                    <tr key={log.id} style={{ borderBottom: '1px solid var(--card-border)' }}>
                                        <td style={{ padding: '8px', color: 'var(--out-of-service)' }}>{new Date(log.timestamp).toLocaleString()}</td>
                                        <td style={{ padding: '8px', fontWeight: 'bold' }}>{log.email}</td>
                                        <td style={{ padding: '8px' }}>{log.action_type}</td>
                                        <td style={{ padding: '8px' }}>{log.details}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                )}

            </div>
        </div>
    );
};

export default AdminPanel;